import React, { useState, useRef } from "react";
import {
  OptimizationAnalysis,
  OptimizationOpportunity,
} from "../../services/processOptimizationAPI";
import {
  Download,
  Users,
  Zap,
  FileText,
  Workflow,
  Shield,
  Brain,
  AlertCircle,
  Check,
  Square,
} from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import toast from "react-hot-toast";
import StakeholderSummary from "./StakeholderSummary";

interface ExecutiveSummaryProps {
  analysis: OptimizationAnalysis;
  projectId: number;
  hideContent?: boolean; // When true, only show download button card, not full content
}

interface QuickWin {
  id: number;
  title: string;
  description: string;
  impact: string;
  effort: string;
  score: number;
  rationale: string;
  icon: string;
}

interface StrategicPriority {
  id: number;
  title: string;
  whyFirst: string; // Business rationale - WHY this one first
  currentPain: string; // Current pain point
  solution: string; // Solution approach
  timeline: string; // Estimated timeline
  unlocks: string; // What downstream items it unlocks
  stepsAffected: number; // Number of process steps affected
  icon: string;
  priorityNumber: number; // 1, 2, or 3
}

const ExecutiveSummary: React.FC<ExecutiveSummaryProps> = ({
  analysis,
  projectId,
  hideContent = false,
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const summaryRef = useRef<HTMLDivElement>(null);

  // Refs for individual sections (for section-wise PDF generation)
  const headerRef = useRef<HTMLDivElement>(null);
  const twoColumnSectionRef = useRef<HTMLDivElement>(null); // Portfolio + Current State Impact
  const businessCaseRef = useRef<HTMLDivElement>(null); // Portfolio Business Case (grounded)
  const phase1Ref = useRef<HTMLDivElement>(null); // Phase 1 Strategic Priorities
  const appendixCardsRefs = useRef<(HTMLDivElement | null)[]>([]); // Individual opportunity cards
  const keyFindingsRef = useRef<HTMLDivElement>(null);
  const readinessSummaryRef = useRef<HTMLDivElement>(null);
  const decisionPointsRef = useRef<HTMLDivElement>(null);

  // Calculate Top 3 Strategic Priorities (replacing Quick Wins)
  const calculateStrategicPriorities = (): StrategicPriority[] => {
    // Get opportunities with no prerequisites (can start immediately)
    const noDependencies = analysis.opportunities.filter(
      (opp) =>
        !opp.dependencies ||
        !opp.dependencies.prerequisites ||
        opp.dependencies.prerequisites.length === 0,
    );

    // If no dependency-free items, use all opportunities
    const candidates =
      noDependencies.length > 0 ? noDependencies : analysis.opportunities;

    const priorities = candidates
      .map((opp, index) => {
        const stepsAffected = opp.affected_steps?.length || 0;
        const timeReduction = parseInt(
          opp.estimated_time_reduction?.match(/\d+/)?.[0] || "0",
        );
        const hasDependents = analysis.opportunities.some((other) =>
          other.dependencies?.prerequisites?.some(
            (p: any) => p.prerequisite_opportunity?.id === opp.id,
          ),
        );
        const dependentCount = analysis.opportunities.filter((other) =>
          other.dependencies?.prerequisites?.some(
            (p: any) => p.prerequisite_opportunity?.id === opp.id,
          ),
        ).length;

        // Determine WHY FIRST rationale
        let whyFirst = "";
        if (hasDependents) {
          whyFirst = `Blocks ${dependentCount} downstream process${dependentCount > 1 ? "es" : ""}, affects ${stepsAffected} step${stepsAffected > 1 ? "s" : ""}`;
        } else if (stepsAffected >= 3) {
          whyFirst = `High-frequency ${opp.inefficiency_type?.toLowerCase() || "process"} affecting ${stepsAffected} critical steps`;
        } else {
          whyFirst = `Foundational for ${analysis.opportunities.length - candidates.length} additional automation${analysis.opportunities.length - candidates.length !== 1 ? "s" : ""}`;
        }

        // Current Pain
        const currentPain = opp.inefficiency_type
          ?.toLowerCase()
          .includes("sequential")
          ? "Sequential bottleneck preventing parallel processing"
          : opp.inefficiency_type?.toLowerCase().includes("manual")
            ? `${timeReduction}% manual effort, manual data entry delays`
            : opp.inefficiency_type?.toLowerCase().includes("handoff")
              ? "Manual triggers causing cross-departmental communication delays"
              : `${opp.inefficiency_type || "Process inefficiency"} impacting workflow velocity`;

        // Solution
        const solution =
          opp.solution_approach ||
          `Automated workflows in ${stepsAffected} affected steps using ${opp.primary_tools?.slice(0, 2).join(", ") || "platform automation"}`;

        // Timeline
        const timeline =
          opp.implementation_complexity === "low"
            ? "4-6 weeks"
            : opp.implementation_complexity === "medium"
              ? "6-10 weeks"
              : "10-16 weeks";

        // Unlocks
        const unlocks = hasDependents
          ? `${dependentCount} downstream optimization${dependentCount > 1 ? "s" : ""}`
          : "Foundation for Phase 2 automations";

        let icon = "workflow";
        if (opp.inefficiency_type?.toLowerCase().includes("manual"))
          icon = "filetext";
        else if (opp.inefficiency_type?.toLowerCase().includes("communication"))
          icon = "users";
        else if (hasDependents) icon = "zap";

        return {
          id: opp.id,
          title: opp.title,
          whyFirst,
          currentPain,
          solution,
          timeline,
          unlocks,
          stepsAffected,
          icon,
          priorityNumber: index + 1,
          score: (hasDependents ? 100 : 0) + stepsAffected * 10 + timeReduction,
        };
      })
      .sort((a, b) => (b as any).score - (a as any).score)
      .slice(0, 3)
      .map((p, i) => ({ ...p, priorityNumber: i + 1 }));

    return priorities;
  };

  // Calculate Top 3 Quick Wins with rationale (legacy - keeping for compatibility)
  const calculateQuickWins = (): QuickWin[] => {
    const quickWinCandidates = analysis.opportunities
      .filter((opp) => opp.implementation_complexity === "low")
      .map((opp) => {
        const workflowScore = opp.workflow_count || 0;
        const timeReduction = parseInt(
          opp.estimated_time_reduction?.match(/\d+/)?.[0] || "0",
        );
        const score = workflowScore * 10 + timeReduction;

        // Generate rationale based on opportunity characteristics
        let rationale = "";
        let icon = "workflow";

        if (opp.inefficiency_type?.toLowerCase().includes("manual")) {
          rationale = `Eliminates ${timeReduction}% of copy/paste errors and zero manual data entry errors.`;
          icon = "filetext";
        } else if (
          opp.inefficiency_type?.toLowerCase().includes("sequential")
        ) {
          rationale = `Convert sequential to parallel approvals. Reduces turnaround to minutes. ${timeReduction}% effort reduction.`;
          icon = "workflow";
        } else if (
          opp.inefficiency_type?.toLowerCase().includes("communication")
        ) {
          rationale = `${timeReduction}% faster departmental handoffs with automated workflows.`;
          icon = "users";
        } else {
          rationale = `${timeReduction}% efficiency gain achievable in 30-60 days.`;
          icon = "zap";
        }

        return {
          id: opp.id,
          title: opp.title,
          description: opp.description || "",
          impact: opp.estimated_time_reduction || "Not specified",
          effort: opp.implementation_complexity,
          score,
          rationale,
          icon,
        };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);

    return quickWinCandidates;
  };

  // Generate Key Findings "SO WHAT" synthesis
  const generateKeyFindings = () => {
    const opportunities = analysis.opportunities;

    const topBottleneck = opportunities.reduce(
      (prev, curr) =>
        (curr.workflow_count || 0) > (prev.workflow_count || 0) ? curr : prev,
      opportunities[0],
    );

    const departmentCounts: { [key: string]: number } = {};
    opportunities.forEach((opp) => {
      const dept = opp.inefficiency_type || "Unknown";
      departmentCounts[dept] = (departmentCounts[dept] || 0) + 1;
    });
    const mostImpactedArea = Object.keys(departmentCounts).reduce((a, b) =>
      departmentCounts[a] > departmentCounts[b] ? a : b,
    );

    const highRiskOpps = opportunities.filter(
      (opp) =>
        opp.compliance_requirements &&
        Object.values(opp.compliance_requirements).some(
          (section: any) => section.risk_level === "high",
        ),
    );

    const aiReadyOpps = opportunities.filter(
      (opp) =>
        opp.ai_integration_opportunities &&
        opp.ai_integration_opportunities.length > 0,
    );

    return {
      bottleneckType: topBottleneck?.inefficiency_type || "Not identified",
      bottleneckDescription: topBottleneck
        ? `${topBottleneck.inefficiency_type} creating multi-step delays in critical paths (e.g., ${topBottleneck.title}).`
        : "No significant bottleneck identified",
      impactedArea: mostImpactedArea,
      impactedDescription: `${mostImpactedArea}, with substantial manual data entry and communication delays.`,
      complianceRisk:
        highRiskOpps.length > 0
          ? `SOX Controls Impacted and Significant Change Management Requirements for automation, requiring formal approval and testing.`
          : "Low compliance risk - standard automation controls apply.",
      aiReadiness: `High potential for Workday's Machine Learning to predict approval outcomes and optimize routing, with ${aiReadyOpps.length} AI Integration Opportunit${aiReadyOpps.length === 1 ? "y" : "ies"} identified.`,
    };
  };

  // Calculate combined quick win value
  const calculateQuickWinValue = () => {
    const qw = quickWins;
    if (qw.length === 0) return { efficiencyGain: "0%", timeToValue: "N/A" };

    const avgImpact =
      qw.reduce((sum, win) => {
        const reduction = parseInt(win.impact.match(/\d+/)?.[0] || "0");
        return sum + reduction;
      }, 0) / qw.length;

    return {
      efficiencyGain: `>${Math.round(avgImpact)}% Efficiency Gain & ${Math.round(avgImpact * 0.9)}% Error Reduction`,
      timeToValue: "Achievable in 30-60 days",
    };
  };

  // Get Pattern Breakdown for Portfolio Snapshot
  const getPatternBreakdown = () => {
    const patternCounts: { [key: string]: number } = {};
    analysis.opportunities.forEach((opp) => {
      const pattern = opp.inefficiency_type || "Unknown";
      patternCounts[pattern] = (patternCounts[pattern] || 0) + 1;
    });
    return patternCounts;
  };

  // Get department areas
  const getDepartments = () => {
    const depts = new Set<string>();
    analysis.opportunities.forEach((opp) => {
      if (opp.inefficiency_type) {
        if (opp.inefficiency_type.toLowerCase().includes("hr")) depts.add("HR");
        if (opp.inefficiency_type.toLowerCase().includes("finance"))
          depts.add("Finance");
        if (opp.inefficiency_type.toLowerCase().includes("it")) depts.add("IT");
      }
    });
    return Array.from(depts).length > 0
      ? Array.from(depts).join(", ")
      : "Cross-Departmental";
  };

  // Translate technical pattern names to business-friendly language
  const translatePattern = (pattern: string): string => {
    const translations: { [key: string]: string } = {
      sequential_bottleneck: "Sequential Bottleneck",
      handoff_delay: "Handoff Delay",
      manual_pattern: "Manual Process",
      exception_handling: "Exception Handling Gap",
      rework_loop: "Rework Loop",
      redundancy: "Redundancy",
      communication_delay: "Communication Delay",
    };
    return translations[pattern.toLowerCase()] || pattern;
  };

  // Format text by removing underscores and capitalizing first letter
  const formatText = (text: string): string => {
    if (!text) return text;
    const withoutUnderscore = text.replace(/_/g, " ");
    return (
      withoutUnderscore.charAt(0).toUpperCase() + withoutUnderscore.slice(1)
    );
  };

  // Calculate portfolio-level financials from grounded opportunities
  const getPortfolioFinancials = () => {
    const grounded = analysis.opportunities.filter(
      (o) => o.data_source === "grounded",
    );
    if (grounded.length === 0) return null;

    const totalAnnualBenefit = grounded.reduce(
      (sum, o) => sum + (Number(o.total_annual_benefit) || 0),
      0,
    );
    const totalNPV = grounded.reduce(
      (sum, o) => sum + (Number(o.npv_3year) || 0),
      0,
    );
    const avgROI =
      grounded.reduce((sum, o) => sum + (Number(o.roi_3year_pct) || 0), 0) /
      grounded.length;
    const withPayback = grounded.filter((o) => o.payback_months);
    const avgPayback =
      withPayback.length > 0
        ? withPayback.reduce(
            (sum, o) => sum + (Number(o.payback_months) || 0),
            0,
          ) / withPayback.length
        : null;

    return {
      groundedCount: grounded.length,
      totalCount: analysis.opportunities.length,
      totalAnnualBenefit,
      totalNPV,
      avgROI,
      avgPayback,
      opportunities: grounded,
    };
  };

  const portfolioFinancials = getPortfolioFinancials();

  const strategicPriorities = calculateStrategicPriorities();
  const quickWins = calculateQuickWins();
  const keyFindings = generateKeyFindings();
  const quickWinValue = calculateQuickWinValue();
  const departments = getDepartments();

  // Get dependent opportunities dynamically from actual data
  const getDependentOpportunities = () => {
    return analysis.opportunities
      .filter(
        (opp) => opp.dependencies && opp.dependencies.prerequisites?.length > 0,
      )
      .map((opp) => ({
        id: opp.id,
        title: opp.title,
        prerequisiteCount: opp.dependencies?.prerequisites?.length || 0,
      }));
  };

  const dependentOpportunities = getDependentOpportunities();

  // Get medium and high complexity opportunities for roadmap phases
  const getMediumComplexityOpps = () => {
    return analysis.opportunities.filter(
      (o) => o.implementation_complexity === "medium",
    );
  };

  const getHighComplexityOpps = () => {
    return analysis.opportunities.filter(
      (o) => o.implementation_complexity === "high",
    );
  };

  const mediumOpps = getMediumComplexityOpps();
  const highOpps = getHighComplexityOpps();

  // Validate step automation overlaps
  const validateStepOverlaps = () => {
    const allStepIds = new Set<number>();
    let totalStepsWithDuplicates = 0;
    const stepOccurrences = new Map<number, string[]>(); // step_id -> opportunity titles

    analysis.opportunities.forEach((opp) => {
      if (opp.affected_steps && opp.affected_steps.length > 0) {
        opp.affected_steps.forEach((step: any) => {
          const stepId = step.id || step.step_id;
          if (stepId) {
            allStepIds.add(stepId);
            totalStepsWithDuplicates++;

            if (!stepOccurrences.has(stepId)) {
              stepOccurrences.set(stepId, []);
            }
            stepOccurrences.get(stepId)?.push(opp.title);
          }
        });
      }
    });

    const uniqueSteps = allStepIds.size;
    const hasOverlaps = totalStepsWithDuplicates > uniqueSteps;
    const overlappingSteps = Array.from(stepOccurrences.entries()).filter(
      ([_, titles]) => titles.length > 1,
    );

    console.log("=== Step Automation Validation ===");
    console.log(
      `Total process steps in workflow: ${analysis.analysis.total_steps}`,
    );
    console.log(`Unique steps being automated: ${uniqueSteps}`);
    console.log(
      `Total step references across opportunities: ${totalStepsWithDuplicates}`,
    );
    console.log(`Overlaps detected: ${hasOverlaps ? "YES" : "NO"}`);

    if (hasOverlaps) {
      console.log(
        `\nOverlapping steps (${overlappingSteps.length} steps affected by multiple opportunities):`,
      );
      overlappingSteps.forEach(([stepId, titles]) => {
        console.log(
          `  Step ${stepId}: automated by ${titles.length} opportunities`,
        );
        titles.forEach((title) => console.log(`    - ${title}`));
      });
    }

    return {
      totalProcessSteps: analysis.analysis.total_steps,
      uniqueStepsAutomated: uniqueSteps,
      totalStepReferences: totalStepsWithDuplicates,
      hasOverlaps,
      overlappingSteps,
      coveragePercentage: Math.round(
        (uniqueSteps / analysis.analysis.total_steps) * 100,
      ),
    };
  };

  const stepValidation = validateStepOverlaps();

  // Export handler - Generate PDF (Section-wise generation for perfect page breaks)
  const handleExport = async () => {
    if (!summaryRef.current) return;

    setIsExporting(true);
    const element = summaryRef.current;
    let originalStyle = "";
    let originalClass = "";

    try {
      // Show loading toast
      toast.loading("Generating PDF... This may take a moment", {
        duration: Infinity,
        id: "pdf-generation",
      });

      // Temporarily make element visible for capture (if hidden)
      originalStyle = element.style.cssText;
      originalClass = element.className;

      if (hideContent) {
        // Make visible temporarily for screenshot but keep off-screen
        element.style.cssText =
          "position: fixed; left: -10000px; top: 0; visibility: visible; pointer-events: none; z-index: -1; opacity: 1;";
        element.className =
          "bg-white rounded-lg shadow-lg overflow-visible border border-gray-200 w-[1200px]";
      }

      // Wait for rendering
      await new Promise((resolve) => setTimeout(resolve, 300));

      // PDF configuration with proper margins
      const pageWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const marginTop = 15;
      const marginBottom = 15;
      const marginLeft = 12;
      const marginRight = 12;
      const usablePageHeight = pageHeight - marginTop - marginBottom;
      const imgWidth = pageWidth - marginLeft - marginRight; // Content width with margins

      // Create PDF
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      let currentY = 0; // Track Y position on current page
      let isFirstPage = true;

      // Section refs to process in order (Appendix handled separately)
      const sectionRefs = [
        { ref: headerRef, name: "Header" },
        { ref: twoColumnSectionRef, name: "Portfolio Overview" },
        { ref: businessCaseRef, name: "Business Case" },
        { ref: phase1Ref, name: "Phase 1 Priorities" },
        { ref: keyFindingsRef, name: "Key Findings" },
        { ref: readinessSummaryRef, name: "Readiness Summary" },
        { ref: decisionPointsRef, name: "Decision Points" },
      ];

      // Process each section
      for (const { ref, name } of sectionRefs) {
        if (!ref.current) {
          console.log(`Skipping ${name} - ref not found`);
          continue;
        }

        console.log(`Processing section: ${name}`);

        // Add delay to prevent browser freeze (longer for Appendix)
        const delay = name === "Appendix" ? 2000 : 100;
        await new Promise((resolve) => setTimeout(resolve, delay));

        // Update loading toast with current section
        toast.loading(
          `Generating PDF... Processing ${name} (this may take 30+ seconds)`,
          {
            duration: Infinity,
            id: "pdf-generation",
          },
        );

        // Generate canvas for this section (reduced scale to prevent freeze)
        const scale = name === "Appendix" ? 0.8 : 1.2; // Lower scale for faster Appendix processing

        console.log(
          `Starting canvas generation for ${name} with scale ${scale}`,
        );
        const canvas = await html2canvas(ref.current, {
          scale: scale,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
          windowWidth: 1250,
          width: ref.current.offsetWidth || 1200,
          height: ref.current.offsetHeight,
          imageTimeout: 0, // No timeout
          removeContainer: true,
          allowTaint: true,
        });
        console.log(
          `Canvas generated for ${name}: ${canvas.width}x${canvas.height}`,
        );

        // Compute card-boundary safe cut points (canvas pixels) to avoid mid-card slicing
        const safeCutPointsPx: number[] = [0, canvas.height];
        {
          const sectionRect = ref.current.getBoundingClientRect();
          const domToCanvas = canvas.height / (ref.current.offsetHeight || 1);
          const cardContainer = ref.current.querySelector(
            ".space-y-6, .space-y-4, .space-y-8, .space-y-3",
          );
          if (cardContainer) {
            Array.from(cardContainer.children).forEach((child) => {
              const el = child as HTMLElement;
              const elRect = el.getBoundingClientRect();
              const topPx = Math.floor(
                (elRect.top - sectionRect.top) * domToCanvas,
              );
              const bottomPx = Math.floor(
                (elRect.bottom - sectionRect.top) * domToCanvas,
              );
              if (topPx > 0) safeCutPointsPx.push(topPx);
              if (bottomPx > 0 && bottomPx < canvas.height)
                safeCutPointsPx.push(bottomPx);
            });
            safeCutPointsPx.sort((a, b) => a - b);
          }
        }

        // Calculate section height in PDF mm maintaining aspect ratio
        const aspectRatio = canvas.height / canvas.width;
        const sectionHeight = imgWidth * aspectRatio;
        console.log(
          `${name} height: ${sectionHeight}mm, currentY: ${currentY}mm`,
        );

        // Check if section fits on current page (with some padding)
        const padding = name === "Header" ? 5 : 8; // Small padding for header, more for other sections
        const spaceNeeded = sectionHeight + padding;

        if (currentY + spaceNeeded > usablePageHeight && !isFirstPage) {
          // Start new page
          console.log(`Starting new page for ${name}`);
          pdf.addPage();
          currentY = marginTop;
        } else if (isFirstPage) {
          currentY = marginTop;
        }

        // If section is too tall for one page, split it with proper canvas slicing
        if (sectionHeight > usablePageHeight) {
          console.log(
            `${name} is too tall (${sectionHeight}mm), splitting with canvas slicing`,
          );

          const canvasHeightInMM = sectionHeight;
          const pixelsPerMM = canvas.height / canvasHeightInMM;
          let sourceY = 0;

          while (sourceY < canvas.height) {
            const availableHeight = usablePageHeight - (currentY - marginTop);
            const maxCutPx = sourceY + Math.floor(availableHeight * pixelsPerMM);
            const remainingPx = canvas.height - sourceY;

            // Snap to last card boundary that fits — avoids cutting through a card
            const fittingPoints = safeCutPointsPx.filter(
              (pt) => pt > sourceY && pt <= maxCutPx,
            );
            const cutPx =
              fittingPoints.length > 0
                ? fittingPoints[fittingPoints.length - 1]
                : Math.min(maxCutPx, sourceY + remainingPx);

            const heightToAddPx = Math.max(1, cutPx - sourceY);
            const heightToAddMM = heightToAddPx / pixelsPerMM;

            // Yield to browser before each heavy slice operation
            await new Promise((resolve) => setTimeout(resolve, 50));

            // Create a temporary canvas for this slice
            const tempCanvas = document.createElement("canvas");
            tempCanvas.width = canvas.width;
            tempCanvas.height = heightToAddPx;
            const tempCtx = tempCanvas.getContext("2d");

            if (tempCtx && heightToAddPx > 0) {
              // Draw only this slice from the original canvas
              tempCtx.drawImage(
                canvas,
                0,
                sourceY,
                canvas.width,
                heightToAddPx,
                0,
                0,
                canvas.width,
                heightToAddPx,
              );

              // Use JPEG for slices — 5-10x faster encoding than PNG for large canvases
              const sliceData = tempCanvas.toDataURL("image/jpeg", 0.88);
              pdf.addImage(
                sliceData,
                "JPEG",
                marginLeft,
                currentY,
                imgWidth,
                heightToAddMM,
                undefined,
                "FAST",
              );
            }

            sourceY += heightToAddPx;

            if (sourceY < canvas.height) {
              // Draw a thin blue line to visually close the section at the page bottom
              pdf.setDrawColor(37, 99, 235);
              pdf.setLineWidth(0.4);
              pdf.line(
                marginLeft,
                currentY + heightToAddMM + 0.8,
                marginLeft + imgWidth,
                currentY + heightToAddMM + 0.8,
              );
              pdf.setDrawColor(0);
              pdf.setLineWidth(0.2);

              pdf.addPage();
              currentY = marginTop;
              await new Promise((resolve) => setTimeout(resolve, 150));
            } else {
              currentY += heightToAddMM + padding; // add section gap after last slice
            }
          }
        } else {
          // Section fits on current page — compute imgData only when needed
          const imgData = canvas.toDataURL("image/jpeg", 0.92);
          pdf.addImage(
            imgData,
            "JPEG",
            marginLeft,
            currentY,
            imgWidth,
            sectionHeight,
            undefined,
            "FAST",
          );
          currentY += sectionHeight + padding;
        }

        isFirstPage = false;
      }

      // Process Appendix card-by-card (page-level approach)
      console.log("Processing Appendix with card-by-card approach");

      // Start new page for Appendix
      pdf.addPage();
      currentY = marginTop;

      // Get the first card element which contains the header
      const firstCardElement = appendixCardsRefs.current[0];
      if (firstCardElement) {
        // Find the header within the parent
        const headerElement =
          firstCardElement.previousElementSibling as HTMLElement;

        if (headerElement) {
          toast.loading("Generating PDF... Processing Appendix Header", {
            duration: Infinity,
            id: "pdf-generation",
          });

          await new Promise((resolve) => setTimeout(resolve, 100));

          const headerCanvas = await html2canvas(headerElement, {
            scale: 2.0,
            useCORS: true,
            logging: false,
            backgroundColor: "#ffffff",
            windowWidth: 1250,
          });

          const headerAspectRatio = headerCanvas.height / headerCanvas.width;
          const headerHeight = imgWidth * headerAspectRatio;

          const headerImgData = headerCanvas.toDataURL("image/png", 0.95);
          pdf.addImage(
            headerImgData,
            "PNG",
            marginLeft,
            currentY,
            imgWidth,
            headerHeight,
            undefined,
            "FAST",
          );
          currentY += headerHeight + 8;
        }
      }

      // Process each opportunity card individually
      const totalCards = appendixCardsRefs.current.filter(
        (ref) => ref !== null,
      ).length;
      for (let i = 0; i < totalCards; i++) {
        const cardRef = appendixCardsRefs.current[i];
        if (!cardRef) continue;

        toast.loading(
          `Generating PDF... Processing Appendix Card ${i + 1}/${totalCards}`,
          {
            duration: Infinity,
            id: "pdf-generation",
          },
        );

        await new Promise((resolve) => setTimeout(resolve, 50));

        const cardCanvas = await html2canvas(cardRef, {
          scale: 1.2,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
          windowWidth: 1250,
        });

        const cardAspectRatio = cardCanvas.height / cardCanvas.width;
        const cardHeight = imgWidth * cardAspectRatio;

        // Check if card fits on current page
        if (currentY + cardHeight > usablePageHeight) {
          pdf.addPage();
          currentY = marginTop;
        }

        const cardImgData = cardCanvas.toDataURL("image/png", 0.85);
        pdf.addImage(
          cardImgData,
          "PNG",
          marginLeft,
          currentY,
          imgWidth,
          cardHeight,
          undefined,
          "FAST",
        );
        currentY += cardHeight + 8;

        console.log(`Added Appendix card ${i + 1}/${totalCards}`);
      }

      // Download PDF
      const fileName = `Executive-Summary-Project-${projectId}-${new Date().toISOString().split("T")[0]}.pdf`;
      pdf.save(fileName);

      // Dismiss loading and show success
      toast.dismiss("pdf-generation");
      toast.success("Executive summary PDF downloaded successfully!");
    } catch (error: any) {
      console.error("PDF export failed:", error);
      toast.dismiss("pdf-generation");
      toast.error(`Failed to export PDF: ${error.message || "Unknown error"}`);
    } finally {
      // Restore original styling
      if (hideContent && element) {
        element.style.cssText = originalStyle;
        element.className = originalClass;
      }
      setIsExporting(false);
    }
  };

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case "users":
        return <Users className="w-5 h-5" />;
      case "filetext":
        return <FileText className="w-5 h-5" />;
      case "workflow":
        return <Workflow className="w-5 h-5" />;
      default:
        return <Zap className="w-5 h-5" />;
    }
  };

  // NumberBadge component for displaying numbered badges
  const NumberBadge: React.FC<{
    value: number;
    size?: number;
    bg?: string;
  }> = ({ value, size = 64, bg = "#2563eb" }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      className="flex-shrink-0"
    >
      <circle cx="50" cy="50" r="50" fill={bg} />
      <text
        x="50"
        y="50"
        dy="0.15em"
        textAnchor="middle"
        dominantBaseline="middle"
        fill="white"
        fontSize="42"
        fontWeight="700"
        fontFamily="Inter, Arial, sans-serif"
      >
        {value}
      </text>
    </svg>
  );

  return (
    <>
      {/* Download Card - Compact version to fit with KPI cards (White background) */}
      {hideContent && (
        <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-5 h-5 text-indigo-900" />
            <span className="text-xs font-semibold text-gray-600 uppercase">
              Executive Summary
            </span>
          </div>
          <div className="mb-3">
            <div className="text-3xl font-bold text-indigo-900">
              {analysis.opportunities.length}
            </div>
            <div className="text-xs text-gray-500">Portfolio Business Case</div>
          </div>
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="w-full inline-flex items-center justify-center gap-2 bg-indigo-900 text-white px-4 py-2 rounded-lg hover:bg-indigo-800 transition-colors disabled:opacity-50 font-semibold text-sm"
          >
            <Download size={16} />
            {isExporting ? "Generating..." : "Download Summary"}
          </button>
        </div>
      )}

      {/* Full Executive Summary Content - Hidden when hideContent is true, but still rendered for PDF generation */}
      <div
        ref={summaryRef}
        className={`bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 ${hideContent ? "fixed -left-[9999px] -top-[9999px] w-[1200px]" : "mb-6"}`}
        style={
          hideContent ? { visibility: "hidden", pointerEvents: "none" } : {}
        }
      >
        {/* Header with Title */}
        <div
          ref={headerRef}
          className="bg-white border-b-4 border-blue-600 p-16"
        >
          <div>
            <h1 className="text-5xl font-bold text-blue-600 leading-tight">
              Process Optimization Executive Summary
            </h1>
            <p className="text-xl text-gray-600 mt-3">
              Linear Verification and Assignment Process Analysis
            </p>
            {/* Portfolio Data Status Badge */}
            {(() => {
              const groundedCount = analysis.opportunities.filter(o => o.data_source === 'grounded').length;
              const totalCount = analysis.opportunities.length;
              const dataStatus = groundedCount === 0 ? 'estimated' : groundedCount === totalCount ? 'grounded' : 'partial';
              return (
                <div className="mt-4">
                  {dataStatus === 'grounded' && (
                    <span
                      title={`All ${totalCount} opportunities grounded with client data`}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold border border-green-300"
                    >
                      ✓ Grounded • All {totalCount} opportunities
                    </span>
                  )}
                  {dataStatus === 'partial' && (
                    <span
                      title={`${groundedCount} of ${totalCount} opportunities have client data`}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold border border-blue-300"
                    >
                      ◐ Partial • {groundedCount}/{totalCount} opportunities grounded
                    </span>
                  )}
                  {dataStatus === 'estimated' && (
                    <span
                      title="AI-generated estimates. Upload metrics data for grounded calculations."
                      className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-semibold border border-amber-300"
                    >
                      🤖 Estimated • Upload metrics for grounded values
                    </span>
                  )}
                </div>
              );
            })()}
          </div>
        </div>

        <div className="px-16 pt-20 pb-16">
          {/* Two Column Layout */}
          <div
            ref={twoColumnSectionRef}
            className="grid grid-cols-1 lg:grid-cols-3 gap-10"
          >
            {/* LEFT COLUMN - Portfolio Summary */}
            <div className="space-y-8 lg:col-span-2 flex flex-col">
              {/* Portfolio Snapshot - Enhanced */}
              <div className="bg-white border-2 border-blue-600 rounded-lg p-10 flex-1 flex flex-col">
                <h2 className="text-5xl font-bold text-gray-900 mb-8 text-center">
                  Portfolio Snapshot
                </h2>

                {/* Big Numbers */}
                <div className="grid grid-cols-2 gap-6 mb-8">
                  <div className="border-2 border-blue-600 rounded-lg p-8 text-center bg-blue-50 min-h-[220px] flex flex-col items-center justify-center">
                    <div className="text-7xl font-bold text-blue-600 mb-4">
                      {analysis.opportunities.length}
                    </div>
                    <div className="text-2xl font-semibold text-gray-900 leading-tight">
                      Optimization Opportunities Identified
                    </div>
                  </div>
                  <div className="border-2 border-blue-600 rounded-lg p-8 text-center bg-blue-50 min-h-[220px] flex flex-col items-center justify-center">
                    <div className="text-7xl font-bold text-blue-600 mb-4">
                      2
                    </div>
                    <div className="text-2xl font-semibold text-gray-900 leading-tight">
                      Implementation Phases
                    </div>
                  </div>
                </div>

                {/* Pattern Distribution */}
                <div className="flex-1">
                  <div className="text-2xl font-bold text-gray-900 mb-4">
                    Pattern Distribution:
                  </div>
                  <div className="space-y-3 text-lg text-gray-700">
                    {Object.entries(getPatternBreakdown()).map(
                      ([pattern, count]) => {
                        const descriptions: { [key: string]: string } = {
                          sequential_bottleneck: "blocking parallel work",
                          handoff_delay: "cross-functional friction",
                          manual_pattern: "automation candidates",
                          exception_handling: "branching logic needed",
                          loop_detection: "rework loop",
                          redundancy: "duplicate systems",
                        };
                        return (
                          <div key={pattern} className="flex items-start gap-2">
                            <span className="text-blue-600">├─</span>
                            <span>
                              <span className="font-semibold">
                                {count} {translatePattern(pattern)}
                              </span>
                              <span className="text-gray-600">
                                {" "}
                                (
                                {descriptions[pattern.toLowerCase()] ||
                                  "process improvement"}
                                )
                              </span>
                            </span>
                          </div>
                        );
                      },
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN - Current State Impact & Strategic Priorities */}
            <div className="space-y-8 lg:col-span-1 flex flex-col">
              {/* Current State Impact Summary */}
              <div className="bg-white border-2 border-blue-600 rounded-lg p-10 flex-1 flex flex-col">
                <h2 className="text-5xl font-bold text-gray-900 mb-8 text-center">
                  Current State Impact
                </h2>
                <div className="text-xl text-gray-700 space-y-4 leading-relaxed">
                  <div>
                    • ~{analysis.analysis.total_steps} linear process steps
                    creating approval chain delays
                  </div>
                  <div>
                    • Multiple stakeholder handoffs requiring manual data entry
                  </div>
                  <div>
                    • Duplicate documentation across SharePoint, Slack, and CRM
                    systems
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* PORTFOLIO BUSINESS CASE - only when grounded data exists */}
          {portfolioFinancials && (
            <div
              ref={businessCaseRef}
              className="bg-white border-2 border-green-600 rounded-lg p-10 mt-10"
            >
              <div className="flex items-center gap-4 mb-8">
                <div className="w-14 h-14 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-3xl font-bold">$</span>
                </div>
                <div>
                  <h2 className="text-5xl font-bold text-gray-900">
                    Portfolio Business Case
                  </h2>
                  <p className="text-xl text-green-700 mt-1">
                    Grounded analysis based on actual client metrics —{" "}
                    {portfolioFinancials.groundedCount} of{" "}
                    {portfolioFinancials.totalCount} opportunities have
                    validated data
                  </p>
                </div>
              </div>

              {/* Key Financial Metrics */}
              <div className="grid grid-cols-4 gap-6 mb-8">
                <div className="border-2 border-green-400 rounded-lg p-6 text-center bg-green-50">
                  <div className="text-5xl font-bold text-green-700">
                    $
                    {portfolioFinancials.totalAnnualBenefit >= 1000000
                      ? `${(portfolioFinancials.totalAnnualBenefit / 1000000).toFixed(1)}M`
                      : `${(portfolioFinancials.totalAnnualBenefit / 1000).toFixed(0)}K`}
                  </div>
                  <div className="text-xl text-gray-700 mt-2 font-semibold">
                    Total Annual Benefit
                  </div>
                </div>
                <div className="border-2 border-green-400 rounded-lg p-6 text-center bg-green-50">
                  <div className="text-5xl font-bold text-green-700">
                    $
                    {portfolioFinancials.totalNPV >= 1000000
                      ? `${(portfolioFinancials.totalNPV / 1000000).toFixed(1)}M`
                      : `${(portfolioFinancials.totalNPV / 1000).toFixed(0)}K`}
                  </div>
                  <div className="text-xl text-gray-700 mt-2 font-semibold">
                    Portfolio NPV (3yr)
                  </div>
                </div>
                <div className="border-2 border-green-400 rounded-lg p-6 text-center bg-green-50">
                  <div className="text-5xl font-bold text-green-700">
                    {portfolioFinancials.avgROI.toFixed(0)}%
                  </div>
                  <div className="text-xl text-gray-700 mt-2 font-semibold">
                    Avg ROI (3-Year)
                  </div>
                </div>
                <div className="border-2 border-green-400 rounded-lg p-6 text-center bg-green-50">
                  <div className="text-5xl font-bold text-green-700">
                    {portfolioFinancials.avgPayback != null
                      ? `${portfolioFinancials.avgPayback.toFixed(1)} mo`
                      : "N/A"}
                  </div>
                  <div className="text-xl text-gray-700 mt-2 font-semibold">
                    Avg Payback Period
                  </div>
                </div>
              </div>

              {/* Per-Opportunity Financial Table */}
              <div className="rounded-lg overflow-hidden border-2 border-green-200">
                <table className="w-full text-lg">
                  <thead>
                    <tr className="bg-green-700 text-white">
                      <th className="text-left p-4 font-bold">Opportunity</th>
                      <th className="text-right p-4 font-bold">Annual Benefit</th>
                      <th className="text-right p-4 font-bold">NPV (3yr)</th>
                      <th className="text-right p-4 font-bold">ROI (3yr)</th>
                      <th className="text-right p-4 font-bold">Payback</th>
                    </tr>
                  </thead>
                  <tbody>
                    {portfolioFinancials.opportunities.map((opp, i) => (
                      <tr
                        key={opp.id}
                        className={i % 2 === 0 ? "bg-white" : "bg-green-50"}
                      >
                        <td className="p-4 text-gray-900 font-medium">
                          {opp.title}
                        </td>
                        <td className="p-4 text-right font-bold text-green-700">
                          $
                          {Number(opp.total_annual_benefit) >= 1000000
                            ? `${(Number(opp.total_annual_benefit) / 1000000).toFixed(1)}M`
                            : `${(Number(opp.total_annual_benefit) / 1000).toFixed(0)}K`}
                        </td>
                        <td className="p-4 text-right font-semibold">
                          $
                          {Number(opp.npv_3year) >= 1000000
                            ? `${(Number(opp.npv_3year) / 1000000).toFixed(1)}M`
                            : `${(Number(opp.npv_3year) / 1000).toFixed(0)}K`}
                        </td>
                        <td className="p-4 text-right font-semibold">
                          {Number(opp.roi_3year_pct).toFixed(0)}%
                        </td>
                        <td className="p-4 text-right font-semibold">
                          {opp.payback_months
                            ? `${Number(opp.payback_months).toFixed(1)} mo`
                            : "N/A"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-6 p-6 bg-green-100 border-l-4 border-green-600 rounded text-lg text-green-900">
                <span className="font-bold">Data Source:</span> All figures
                calculated using the Grounded Calculation Engine (v1.0) with
                10% discount rate and 3-year horizon. Values derived from
                client-provided operational metrics.
              </div>
            </div>
          )}

          {/* Phase 1: Strategic Priorities - Full Width */}
          <div
            ref={phase1Ref}
            className="bg-white border-2 border-blue-600 rounded-lg p-10"
          >
            <h2 className="text-5xl font-bold text-gray-900 mb-4 text-center">
              Phase 1: Strategic Priorities
            </h2>
            <p className="text-2xl text-blue-700 italic mb-8 text-center">
              Foundation Builders (No upstream dependencies)
            </p>

            <div className="space-y-6">
              {strategicPriorities.map((priority) => (
                <div
                  key={priority.id}
                  className="bg-white border-2 border-blue-200 rounded-lg p-6"
                >
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-3xl font-bold inline-flex items-center justify-center translate-y-[0px]">
                        <NumberBadge value={priority.priorityNumber} />
                      </span>
                    </div>

                    <h3 className="font-bold text-3xl text-gray-900">
                      {priority.title}
                    </h3>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <div className="font-bold text-lg text-blue-700 mb-2 uppercase">
                        Why Now
                      </div>
                      <div className="text-lg text-gray-700">
                        {priority.whyFirst}
                      </div>
                    </div>

                    <div>
                      <div className="font-bold text-lg text-gray-900 mb-2 uppercase">
                        Current Pain Point
                      </div>
                      <div className="text-lg text-gray-700">
                        {priority.currentPain}
                      </div>
                    </div>

                    <div>
                      <div className="font-bold text-lg text-gray-900 mb-2 uppercase">
                        Solution Approach
                      </div>
                      <div className="text-lg text-gray-700">
                        {priority.solution}
                      </div>
                    </div>

                    <div>
                      <div className="font-bold text-lg text-gray-900 mb-2 uppercase">
                        Prerequisites
                      </div>
                      <div className="text-lg text-gray-700">
                        None (Ready to start)
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {strategicPriorities.length === 0 && (
                <div className="text-center py-8 text-gray-500 text-xl">
                  All opportunities have prerequisites - see phased approach
                  below.
                </div>
              )}
            </div>
          </div>

          {/* KEY FINDINGS */}
          <div
            ref={keyFindingsRef}
            className="bg-white border-2 border-gray-300 rounded-lg p-12 mt-10"
          >
            <h2 className="text-5xl font-bold text-gray-900 mb-12 text-center">
              Key Findings
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              {/* Most Significant Bottleneck Type */}
              <div className="space-y-4">
                <div className="font-bold text-2xl text-blue-900 uppercase tracking-wide">
                  MOST SIGNIFICANT BOTTLENECK TYPE
                </div>
                <div className="text-xl text-gray-700 leading-relaxed">
                  {formatText(keyFindings.bottleneckType)} causing multi-step
                  delays where parallel execution is possible (e.g., steps 23
                  requiring serial approvals before organizational structure
                  validation).
                </div>
              </div>

              {/* Most Impacted Process Area */}
              <div className="space-y-4">
                <div className="font-bold text-2xl text-blue-900 uppercase tracking-wide">
                  MOST IMPACTED PROCESS AREA
                </div>
                <div className="text-xl text-gray-700 leading-relaxed">
                  {formatText(keyFindings.impactedArea)} workflows concentrated
                  in this area
                  <div className="mt-3 space-y-2 text-lg">
                    <div>
                      → {analysis.opportunities.length} opportunities identified
                    </div>
                    <div>
                      → {analysis.analysis.total_steps} total process steps
                      affected
                    </div>
                    <div>
                      → {dependentOpportunities.length} dependencies across
                      approval chains
                    </div>
                  </div>
                </div>
              </div>

              {/* Compliance & Risk Considerations */}
              <div className="space-y-4">
                <div className="font-bold text-2xl text-blue-900 uppercase tracking-wide">
                  COMPLIANCE & RISK CONSIDERATIONS
                </div>
                <div className="text-xl text-gray-700 leading-relaxed">
                  {analysis.opportunities.filter(
                    (o) =>
                      o.compliance_requirements &&
                      Object.keys(o.compliance_requirements).length > 0,
                  ).length > 0
                    ? "MEDIUM"
                    : "LOW"}{" "}
                  risk profile with{" "}
                  {
                    analysis.opportunities.filter(
                      (o) => o.compliance_requirements,
                    ).length
                  }{" "}
                  opportunities requiring compliance review
                  <div className="mt-3 space-y-2 text-lg">
                    <div>• Audit trail requirements for all approvals</div>
                    <div>• Data governance and validation standards</div>
                    <div>→ All solutions maintain compliance documentation</div>
                  </div>
                </div>
              </div>

              {/* AI Readiness Assessment */}
              <div className="space-y-4">
                <div className="font-bold text-2xl text-blue-900 uppercase tracking-wide">
                  AI READINESS ASSESSMENT
                </div>
                <div className="text-xl text-gray-700 leading-relaxed">
                  HIGH potential across{" "}
                  {
                    analysis.opportunities.filter(
                      (o) =>
                        o.ai_integration_opportunities &&
                        o.ai_integration_opportunities.length > 0,
                    ).length
                  }{" "}
                  identified opportunities
                  <div className="mt-3 space-y-2 text-lg">
                    <div>• Workday ML for predictive approval routing</div>
                    <div>• Skills Cloud for talent matching in assignments</div>
                    <div>• Embedded collaboration and optimization</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* IMPLEMENTATION READINESS */}
          <div
            ref={readinessSummaryRef}
            className="mt-32 bg-white border-2 border-gray-300 rounded-lg p-12"
          >
            <h2 className="text-5xl font-bold text-gray-900 mb-16 text-center">
              Implementation Readiness
            </h2>

            <div className="max-w-4xl mx-auto space-y-10">
              {/* Ready to Start */}
              <div className="bg-green-50 rounded-lg p-10 border-2 border-green-600">
                <div className="flex items-center gap-4 mb-8">
                  <div className="flex items-center justify-center w-16 h-16 rounded bg-green-600">
                    <Check className="w-8 h-8 text-white stroke-[3]" />
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900">
                    Phase 1 Ready to Start ({strategicPriorities.length}{" "}
                    Opportunities)
                  </h3>
                </div>
                <div className="space-y-4 text-xl text-gray-800">
                  <div className="flex items-start gap-3">
                    <span className="text-green-600 font-bold text-xl">✓</span>
                    <span>
                      {strategicPriorities.length} opportunities with no
                      upstream blockers identified
                    </span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-green-600 font-bold text-xl">✓</span>
                    <span>Technical requirements documented</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-green-600 font-bold text-xl">✓</span>
                    <span>Existing Workday licensing sufficient</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-green-600 font-bold text-xl">✓</span>
                    <span>
                      {analysis.opportunities
                        .filter(
                          (o) =>
                            !o.dependencies?.prerequisites ||
                            o.dependencies.prerequisites.length === 0,
                        )
                        .reduce(
                          (sum, o) =>
                            sum + (o.dependencies?.prerequisites?.length || 0),
                          0,
                        )}{" "}
                      total prerequisites mapped
                    </span>
                  </div>
                </div>
                <div className="mt-10 p-8 bg-white rounded border border-green-300 flex items-center">
                  <div className="grid grid-cols-3 gap-8 w-full text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="text-5xl font-bold text-green-700 flex items-center justify-center">
                        {strategicPriorities.length}
                      </div>
                      <div className="text-xl text-gray-600 mt-6">
                        Opportunities
                      </div>
                    </div>
                    <div className="flex flex-col items-center justify-center">
                      <div className="text-5xl font-bold text-green-700 flex items-center justify-center">
                        27
                      </div>
                      <div className="text-xl text-gray-600 mt-6">
                        Prerequisites
                      </div>
                    </div>
                    <div className="flex flex-col items-center justify-center">
                      <div className="text-5xl font-bold text-green-700 flex items-center justify-center">
                        4-6
                      </div>
                      <div className="text-xl text-gray-600 mt-6">Weeks</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Requires Phase 1 */}
              <div className="bg-orange-50 rounded-lg p-10 border-2 border-orange-400">
                <div className="flex items-center gap-4 mb-8">
                  <NumberBadge value={2} bg="#f97316" />
                  <h3 className="text-3xl font-bold text-gray-900">
                    Phase 2 Opportunities ({mediumOpps.length + highOpps.length}{" "}
                    Opportunities)
                  </h3>
                </div>
                <div className="space-y-4 text-xl text-gray-800">
                  <div className="flex items-start gap-3">
                    <span className="text-orange-600 font-bold text-xl">⚠</span>
                    <span>
                      {mediumOpps.length + highOpps.length} opportunities
                      requiring Phase 1 completion
                    </span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-orange-600 font-bold text-xl">⚠</span>
                    <span>
                      Medium-High complexity (API integration, branching logic)
                    </span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-orange-600 font-bold text-xl">⚠</span>
                    <span>13 mapped dependencies across Phase 1 items</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-orange-600 font-bold text-xl">⚠</span>
                    <span>Dependencies analyzed & sequenced</span>
                  </div>
                </div>
                <div className="mt-10 p-8 bg-white rounded border border-orange-300 flex items-center">
                  <div className="grid grid-cols-3 gap-8 w-full text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="text-5xl font-bold text-orange-700 flex items-center justify-center">
                        {mediumOpps.length + highOpps.length}
                      </div>
                      <div className="text-xl text-gray-600 mt-6">
                        Opportunities
                      </div>
                    </div>
                    <div className="flex flex-col items-center justify-center">
                      <div className="text-5xl font-bold text-orange-700 flex items-center justify-center">
                        38
                      </div>
                      <div className="text-xl text-gray-600 mt-6">
                        Prerequisites
                      </div>
                    </div>
                    <div className="flex flex-col items-center justify-center">
                      <div className="text-5xl font-bold text-orange-700 flex items-center justify-center">
                        8-12
                      </div>
                      <div className="text-xl text-gray-600 mt-6">Weeks</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* EXECUTIVE DECISION POINTS */}
          <div
            ref={decisionPointsRef}
            className="mt-24 bg-orange-50 border-2 border-orange-600 rounded-lg p-12"
          >
            <div className="flex items-center gap-4 mb-10">
              <div className="flex items-center justify-center w-14 h-14 rounded bg-orange-600">
                <AlertCircle className="w-7 h-7 text-white stroke-[3]" />
              </div>
              <h2 className="text-5xl font-bold text-gray-900">
                Executive Decision Points
              </h2>
            </div>

            <div className="space-y-12">
              {/* Leadership Input Required */}
              <div>
                <div className="text-3xl font-bold text-gray-900 mb-8 uppercase">
                  Leadership Input Required:
                </div>

                <div className="space-y-6 text-xl">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded bg-orange-600">
                      <Square className="w-5 h-5 text-white stroke-[2]" />
                    </div>
                    <div className="flex-1 text-gray-800 leading-relaxed">
                      <span className="font-bold">
                        Approve Phase 1 resource allocation
                      </span>{" "}
                      - Department heads + Workday Admin for 1 priority
                      implementations
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded bg-orange-600">
                      <Square className="w-5 h-5 text-white stroke-[2]" />
                    </div>
                    <div className="flex-1 text-gray-800 leading-relaxed">
                      <span className="font-bold">
                        Prioritize if scheduling conflicts arise
                      </span>{" "}
                      - All Phase 1 items are marked medium complexity
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded bg-orange-600">
                      <Square className="w-5 h-5 text-white stroke-[2]" />
                    </div>
                    <div className="flex-1 text-gray-800 leading-relaxed">
                      <span className="font-bold">
                        Validate compliance approach
                      </span>{" "}
                      - Review requirements for automation controls
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded bg-orange-600">
                      <Square className="w-5 h-5 text-white stroke-[2]" />
                    </div>
                    <div className="flex-1 text-gray-800 leading-relaxed">
                      <span className="font-bold">
                        Confirm implementation timeline
                      </span>{" "}
                      - Phase 1 (4-10 weeks) → Phase 2 (8-16 weeks additional)
                    </div>
                  </div>
                </div>
              </div>

              {/* Analysis Limitations */}
              <div>
                <div className="text-3xl font-bold text-gray-900 mb-8 uppercase">
                  Analysis Limitations:
                </div>

                <div className="space-y-6 text-xl">
                  {portfolioFinancials ? (
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 text-green-600 text-3xl">
                        ✓
                      </div>
                      <div className="flex-1 text-gray-800">
                        <span className="font-bold text-green-700">
                          Financial impact — Grounded data available
                        </span>{" "}
                        — $
                        {portfolioFinancials.totalAnnualBenefit >= 1000000
                          ? `${(portfolioFinancials.totalAnnualBenefit / 1000000).toFixed(1)}M`
                          : `${(portfolioFinancials.totalAnnualBenefit / 1000).toFixed(0)}K`}{" "}
                        total annual benefit, {portfolioFinancials.avgROI.toFixed(0)}% avg ROI across{" "}
                        {portfolioFinancials.groundedCount} opportunities
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 text-orange-600 text-3xl">
                        ⚠
                      </div>
                      <div className="flex-1 text-gray-800">
                        <span className="font-bold">
                          Financial impact modeling
                        </span>{" "}
                        - Requires operational cost data, budget analysis, and ROI
                        calculations
                      </div>
                    </div>
                  )}

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 text-orange-600 text-3xl">
                      ⚠
                    </div>
                    <div className="flex-1 text-gray-800">
                      <span className="font-bold">
                        Resource capacity assessment
                      </span>{" "}
                      - Requires team availability data, skillset mapping, and
                      bandwidth analysis
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 text-orange-600 text-3xl">
                      ⚠
                    </div>
                    <div className="flex-1 text-gray-800">
                      <span className="font-bold">
                        Change management planning
                      </span>{" "}
                      - Requires stakeholder analysis, training needs assessment
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recommendation */}
            <div className="mt-10 p-8 bg-blue-100 border-l-4 border-blue-600 rounded">
              <div className="font-bold text-blue-900 mb-3 text-xl">
                → Recommended Next Step:
              </div>
              <div className="text-lg text-gray-800">
                {portfolioFinancials
                  ? `Business case validated with grounded data ($${portfolioFinancials.totalAnnualBenefit >= 1000000 ? `${(portfolioFinancials.totalAnnualBenefit / 1000000).toFixed(1)}M` : `${(portfolioFinancials.totalAnnualBenefit / 1000).toFixed(0)}K`} annual benefit). Proceed to Phase 1 resource allocation and implementation kickoff.`
                  : "Stakeholder workshop to quantify business case and validate resource commitments before Phase 1 kickoff"}
              </div>
            </div>
          </div>

          {/* STAKEHOLDER & GOVERNANCE SUMMARY */}
          <div className="mt-24">
            <StakeholderSummary opportunities={analysis.opportunities} />
          </div>

          {/* APPENDIX: Complete Optimization Portfolio - Moved to End */}
          <div className="mt-24 pb-16">
            {analysis.opportunities.map((opp, index) => {
              const isFirstCard = index === 0;
              const categoryColor = opp.inefficiency_type
                ?.toLowerCase()
                .includes("sequential")
                ? "bg-red-100 text-red-800 border-red-300"
                : opp.inefficiency_type?.toLowerCase().includes("handoff")
                  ? "bg-orange-100 text-orange-800 border-orange-300"
                  : opp.inefficiency_type?.toLowerCase().includes("manual")
                    ? "bg-blue-100 text-blue-800 border-blue-300"
                    : opp.inefficiency_type?.toLowerCase().includes("exception")
                      ? "bg-yellow-100 text-yellow-800 border-yellow-300"
                      : opp.inefficiency_type?.toLowerCase().includes("loop")
                        ? "bg-purple-100 text-purple-800 border-purple-300"
                        : "bg-gray-100 text-gray-800 border-gray-300";

              // Generate improved solution description
              const generateImprovedSolution = () => {
                const inefficiencyType =
                  opp.inefficiency_type?.toLowerCase() || "";
                const tools =
                  opp.primary_tools?.slice(0, 2).join(" and ") ||
                  "automation tools";
                const stepsCount = opp.affected_steps?.length || 0;

                let improvedSolution = "";

                if (inefficiencyType.includes("manual")) {
                  improvedSolution = `Use ${tools} to automatically populate data across all ${stepsCount} affected process steps, eliminating manual data entry and ensuring real-time synchronization across all systems.`;
                } else if (inefficiencyType.includes("sequential")) {
                  improvedSolution = `Configure ${tools} to enable parallel processing workflows, allowing ${stepsCount} steps to execute simultaneously rather than sequentially, dramatically reducing approval wait times.`;
                } else if (inefficiencyType.includes("handoff")) {
                  improvedSolution = `Implement automated triggers using ${tools} to instantly notify relevant stakeholders and push documents through the ${stepsCount}-step workflow without manual intervention or departmental delays.`;
                } else if (inefficiencyType.includes("exception")) {
                  improvedSolution = `Build conditional logic flows in ${tools} to automatically route exceptions through appropriate approval paths, handling the ${stepsCount} affected steps based on predefined business rules.`;
                } else if (
                  inefficiencyType.includes("loop") ||
                  inefficiencyType.includes("rework")
                ) {
                  improvedSolution = `Deploy validation workflows in ${tools} to catch errors at the source across ${stepsCount} process steps, preventing rework loops before they occur.`;
                } else {
                  improvedSolution =
                    opp.solution_approach ||
                    `Automate the process using ${tools} to streamline ${stepsCount} workflow steps and eliminate inefficiencies.`;
                }

                return improvedSolution;
              };

              return (
                <React.Fragment key={opp.id}>
                  {/* Appendix Header - only before first card */}
                  {isFirstCard && (
                    <div className="bg-white border-2 border-blue-600 rounded-lg p-12 mb-6">
                      <h2 className="text-5xl font-bold text-gray-900 mb-6 text-center">
                        Appendix: Complete Optimization Portfolio
                      </h2>
                      <p className="text-xl text-gray-600 mb-0 text-center">
                        All {analysis.opportunities.length} identified opportunities with
                        problem statements, solutions, and affected process steps
                      </p>
                    </div>
                  )}

                  <div
                    ref={(el) => (appendixCardsRefs.current[index] = el)}
                    className="bg-white border-2 border-blue-200 rounded-lg p-10 mb-10"
                  >
                    {/* Card Header with Number and Title */}
                  <div className="flex items-start gap-5 mb-8">
                    <NumberBadge value={index + 1} />
                    <div className="flex-1">
                      <h3 className="font-bold text-2xl text-gray-900 mb-4 leading-tight">
                        {opp.title}
                      </h3>
                      {/* It's working correct, don't touch it*/}
                      <div
                        className={`inline-block px-6 pb-6 pt-3 rounded border-2 ${categoryColor} text-center`}
                      >
                        <div className="text-lg font-bold uppercase tracking-wide opacity-90">
                          {translatePattern(opp.inefficiency_type || "Unknown")}
                        </div>
                        <div className="text-lg font-bold uppercase">
                          {/* {translatePattern(opp.inefficiency_type || "Unknown")} */}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Problem Statement */}
                  <div className="mb-8">
                    <div className="font-bold text-xl text-blue-900 mb-4 uppercase tracking-wide">
                      PROBLEM
                    </div>
                    <div className="text-gray-700 leading-relaxed text-lg">
                      {opp.description}
                    </div>
                  </div>

                  {/* Solution */}
                  <div className="mb-8">
                    <div className="font-bold text-xl text-blue-900 mb-4 uppercase tracking-wide">
                      SOLUTION
                    </div>
                    <div className="text-lg text-gray-700 leading-relaxed">
                      {generateImprovedSolution()}
                    </div>
                  </div>

                  {/* Steps Impacted */}
                  <div className="border-t-2 border-blue-200 pt-8">
                    <div className="grid grid-cols-2 gap-8">
                      <div>
                        <div className="font-bold text-xl text-gray-900 uppercase tracking-wide mb-3">
                          STEPS IMPACTED
                        </div>
                        <div className="text-gray-700 text-lg font-semibold">
                          {opp.affected_steps?.length || 0} Steps Affected
                        </div>
                      </div>
                      <div>
                        <div className="font-bold text-xl text-gray-900 uppercase tracking-wide mb-3">
                          TIME REDUCTION
                        </div>
                        <div className="text-gray-700 text-lg font-semibold">
                          {opp.estimated_time_reduction || "To be determined"}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Financial Data - shown only when grounded */}
                  {opp.data_source === "grounded" && (
                    <div className="mt-6 border-t-2 border-green-200 pt-6">
                      <div className="flex items-center gap-2 mb-4">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-green-100 text-green-800 border border-green-300">
                          ✓ Grounded ROI
                        </span>
                      </div>
                      <div className="grid grid-cols-4 gap-4">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                          <div className="text-2xl font-bold text-green-700">
                            $
                            {Number(opp.total_annual_benefit) >= 1000000
                              ? `${(Number(opp.total_annual_benefit) / 1000000).toFixed(1)}M`
                              : `${(Number(opp.total_annual_benefit) / 1000).toFixed(0)}K`}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            Annual Benefit
                          </div>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                          <div className="text-2xl font-bold text-green-700">
                            $
                            {Number(opp.npv_3year) >= 1000000
                              ? `${(Number(opp.npv_3year) / 1000000).toFixed(1)}M`
                              : `${(Number(opp.npv_3year) / 1000).toFixed(0)}K`}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            NPV (3yr)
                          </div>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                          <div className="text-2xl font-bold text-green-700">
                            {Number(opp.roi_3year_pct).toFixed(0)}%
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            ROI (3yr)
                          </div>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                          <div className="text-2xl font-bold text-green-700">
                            {opp.payback_months
                              ? `${Number(opp.payback_months).toFixed(1)} mo`
                              : "N/A"}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            Payback
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default ExecutiveSummary;
